import React, { useState, useEffect } from 'react';
import { Search, X, ChevronRight } from 'lucide-react';
import '../FullAnalysisModal/FullAnalysisModal.css';
import './ArticlePage.css';

// getArticleList API is not yet available
const getArticleList = async () => ({ list: [] });

// updatedAt ISO 문자열 → "2026.04.04" 포맷
const formatDate = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d)) return isoStr;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

// 네이티브 브릿지로 PDF URL 전달
const triggerPdfBridge = (pdfUrl) => {
  console.log('PDF URL 전달:', pdfUrl);
  if (window.webkit?.messageHandlers?.openPdfArticle) {
    window.webkit.messageHandlers.openPdfArticle.postMessage(pdfUrl); // iOS
  } else if (window.openPdfArticle) {
    window.openPdfArticle.postMessage(pdfUrl); // Android
  } else {
    window.open(pdfUrl, '_blank'); // 웹 폴백
  }
};

const ArticlePage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const data = await getArticleList();
        setArticles(data.list || []);
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  // 300ms 디바운스 — 필터만 지연, 칩 표시는 즉시
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedFilter(searchTerm.trim().toUpperCase());
    }, 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  const displayChip = searchTerm.trim().toUpperCase();

  // 클라이언트 필터링: title에 검색어 포함
  const filtered = debouncedFilter
    ? articles.filter((a) => a.title.toUpperCase().includes(debouncedFilter))
    : articles;

  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedFilter('');
  };

  return (
    <div className="article-page">
      {/* 헤더 — far-header 패턴 재사용 */}
      <div className="far-header">
        <div className="far-header-left">
          <span className="far-header-report-type">AI ARTICLE</span>
          <div className="far-header-title-row">
            <span className="far-header-title">AI 투자 리포트</span>
          </div>
        </div>
      </div>

      {/* 검색바 */}
      <div className="article-search-wrap">
        <div className="article-search-bar">
          <Search size={16} className="article-search-icon" />
          <input
            type="text"
            className="article-search-input"
            placeholder="키워드로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          {displayChip && (
            <span className="article-search-chip">
              {displayChip}
              <button onClick={clearSearch} aria-label="필터 해제">
                <X size={11} />
              </button>
            </span>
          )}
        </div>
      </div>

      {/* 아티클 목록 */}
      <div className="article-body">
        {loading ? (
          [1, 2, 3].map((i) => (
            <div key={i} className="rep-skeleton-card">
              <div className="rep-skeleton rep-skeleton--tag" />
              <div className="rep-skeleton rep-skeleton--title-1" />
              <div className="rep-skeleton rep-skeleton--title-2" />
              <div className="rep-skeleton rep-skeleton--summary" />
              <div className="rep-skeleton rep-skeleton--date" />
            </div>
          ))
        ) : filtered.length > 0 ? (
          filtered.map((article) => (
            <div
              key={article.articleNo}
              className="rep-card rep-card--stock"
              onClick={() => triggerPdfBridge(article.pdfUrl)}
            >
              <div className="rep-card-body">
                <div className="rep-card-tag">AI 리포트</div>
                <div className="rep-card-title">{article.title}</div>
                {article.summary && (
                  <div className="rep-card-summary">{article.summary}</div>
                )}
                <div className="rep-card-meta">
                  <span className="rep-card-date">{formatDate(article.updatedAt)}</span>
                </div>
              </div>
              <ChevronRight size={18} className="rep-card-arrow" />
            </div>
          ))
        ) : error ? (
          <div className="article-empty">
            <div className="article-empty-icon">⚠️</div>
            <h3 className="article-empty-title">리포트를 불러오지 못했습니다</h3>
            <p className="article-empty-desc">네트워크 연결을 확인하고 다시 시도해주세요.</p>
          </div>
        ) : (
          <div className="article-empty">
            <div className="article-empty-icon">📄</div>
            <h3 className="article-empty-title">
              {displayChip ? `'${displayChip}' 검색 결과 없음` : '리포트가 없습니다'}
            </h3>
            <p className="article-empty-desc">
              {displayChip
                ? '다른 키워드로 검색해보세요.'
                : 'AI 리포트가 곧 업로드됩니다.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticlePage;
